<template>
    <div v-if="hideForm">
        <div
            class="columns is-mobile is-multiline is-variable is-1 is-align-items-flex-end"
        >
            <div
                class="column is-6-tablet is-1-desktop"
                v-if="!isAutoGenerated"
            >
                <b-field label="Nº Doc">
                    <Input
                        v-model="edit.document_number"
                        placeholder="20000"
                        rules="required|max:50"
                    />
                </b-field>
            </div>
            <div class="column is-6-tablet is-2-desktop" v-if="!isContribution">
                <b-field :label="rutLabel" label-for="counterpartRut">
                    <Input
                        inputId="counterpartRut"
                        v-model="edit.counterpart.rut"
                        placeholder="11.111.111-1"
                        rules="required|min:11|max:12|rut"
                        v-mask="rutMask"
                    />
                </b-field>
            </div>
            <div
                class="column is-12-tablet is-2-desktop"
                :class="[
                    { 'is-5-desktop': isContribution },
                    { 'is-3-desktop': isSalary },
                ]"
            >
                <label for="counterpartName" class="label">
                    {{ nameCounterpartLabel }}
                </label>
                <Input
                    inputId="counterpartName"
                    v-model="edit.counterpart.name"
                    :placeholder="isRendition ? 'Nombre' : 'Empresa CFO Remoto'"
                    rules="required"
                    :disabled="isContribution"
                />
            </div>
            <div class="column is-12-tablet is-2-desktop" v-if="isRendition">
                <label for="description" class="label">
                    Descripción
                </label>
                <Input
                    inputId="description"
                    v-model="edit.description"
                    placeholder="Detalle de lo rendido"
                    rules="required"
                    :disabled="!isRendition"
                />
            </div>
            <div class="column is-12-tablet is-4-desktop">
                <div
                    class="columns is-mobile is-variable is-12-mobile is-1-desktop is-align-items-flex-end"
                >
                    <div
                        class="column is-4-mobile is-6-desktop"
                        v-if="hasRecurrency"
                    >
                        <b-field label="Día de pago">
                            <DayPicker
                                placeholder="Selecciona día"
                                v-model="edit.recurrency.payment_day"
                                :selectableDays="
                                    edit.obligation_type.associated_emitted_at
                                "
                            />
                        </b-field>
                    </div>
                    <div
                        class="column is-4-mobile is-5-desktop"
                        :class="isContribution ? 'is-6-desktop' : ''"
                        v-else
                    >
                        <b-field :label="calendarLabel">
                            <b-datepicker
                                placeholder="Selecciona fecha emisión."
                                v-bind="initDatepicker"
                                v-model="edit.emitted_at"
                                :selectable-dates="selectableDates"
                                @change-month="onChangeDateWrapper"
                                @change-year="onChangeDateWrapper"
                            />
                        </b-field>
                    </div>
                    <div
                        class="column is-4-mobile is-3-desktop"
                        v-if="!isContribution && !isSalary"
                    >
                        <b-field label="Plazo (días)">
                            <Input
                                rules="required|min:0"
                                type="number"
                                name="Plazo"
                                min="1"
                                vid="payment_period"
                                v-model="edit.payment_period"
                                placeholder="30"
                            />
                        </b-field>
                    </div>
                    <div
                        class="column is-4-mobile is-4-desktop"
                        :class="
                            isContribution || isSalary ? 'is-6-desktop' : ''
                        "
                    >
                        <b-field
                            label="Tipo"
                            :class="{ disabled: isTypeDisabled }"
                        >
                            <b-select
                                v-model="edit.document_type"
                                expanded
                                :disabled="isTypeDisabled"
                            >
                                <option value="P">Por pagar</option>
                                <option value="C">Por cobrar</option>
                            </b-select>
                        </b-field>
                    </div>
                </div>
            </div>
            <div
                class="column is-6-mobile is-1-desktop"
                :class="[{ 'is-2-desktop': !isRendition }]"
            >
                <div class="field">
                    <label for="amount" class="label">
                        {{ amountLabel }}
                    </label>
                    <Input
                        inputId="amount"
                        v-model.lazy="edit.amount"
                        v-money="moneyConfig"
                        placeholder="$200.000"
                        rules="required|positive-money|max:13"
                    />
                </div>
            </div>
            <div class="column is-6-mobile is-1-desktop is-align-self-flex-end">
                <button
                    class="button is-flex is-fullwidth is-primary"
                    :disabled="loading"
                    v-on:click.prevent="
                        handleSubmitWrapper(callCreateTaxDocWrapper)
                    "
                >
                    Agregar
                </button>
            </div>
            <div
                class="column is-12-tablet is-offset-5-desktop is-6-desktop"
                v-if="hasRecurrency"
            >
                <div
                    class="columns is-mobile is-variable is-12-tablet is-1-desktop is-align-items-flex-end"
                >
                    <b-field label="Contrato" class="column is-4-mobile mb-0">
                        <b-select
                            id="contrato"
                            v-model="edit.recurrency.indefinite_term"
                            expanded
                        >
                            <option :value="false">Plazo Fijo</option>
                            <option :value="true">Plazo Indefinido</option>
                        </b-select>
                    </b-field>
                    <b-field
                        class="column is-4-mobile mb-0"
                        label="Fecha de Ingreso"
                    >
                        <b-datepicker
                            placeholder="Selecciona fecha de ingreso."
                            v-bind="initDatepicker"
                            v-model="edit.recurrency.valid_from"
                        />
                    </b-field>
                    <b-field
                        class="column is-4-mobile mb-0"
                        label="Fecha de Término"
                    >
                        <b-datepicker
                            placeholder="Selecciona fecha de término."
                            v-bind="initDatepicker"
                            v-model="edit.recurrency.valid_until"
                            :min-date="minValidFrom"
                            :disabled="edit.recurrency.indefinite_term"
                        />
                    </b-field>
                </div>
            </div>
        </div>
        <div class="columns is-mobile mt-2 mx-0" v-if="hasRecurrency">
            <div class="column is-12 has-background-light has-text-justified">
                <p class="p-2">
                    Los sueldos de plazo fijo e indefinido que se mostrarán en
                    la tabla y en el gráfico corresponden a los próximos 3
                    meses. Según el tiempo definido se agregarán cada mes nuevos
                    sueldos hasta que termine el plazo o hasta que se edite la
                    recurrencia del sueldo.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import DayPicker from "@/components/inputs/DayPicker";
import Input from "@/components/inputs/Input";
import { configMoney } from "@/utils/currency";
import { Money } from "@/directives/money";

export default {
    name: "FormObligations",
    components: {
        Input,
        DayPicker,
    },
    data() {
        return {
            moneyConfig: configMoney,
        };
    },
    props: [
        "edit",
        "isAutoGenerated",
        "isContribution",
        "rutLabel",
        "isSalary",
        "isRendition",
        "hasRecurrency",
        "initDatepicker",
        "minValidFrom",
        "rutMask",
        "nameCounterpartLabel",
        "calendarLabel",
        "selectableDates",
        "isTypeDisabled",
        "amountLabel",
        "loading",
        "callCreateTaxDocument",
        "hideForm",
    ],
    methods: {
        onChangeDateWrapper(value) {
            this.$emit("on-change-date", value);
        },
        handleSubmitWrapper(value) {
            this.$emit("handle-submit", value);
        },
        callCreateTaxDocWrapper() {
            this.$emit("call-create-tax-document");
        },
    },
    directives: { money: Money },
};
</script>
